
import { useState, useEffect } from 'react';
import Loader from '@/components/ui/Loader';
import VideoCardWrapper from '@/components/layout/VideoCardWrapper';
import { Video } from '@/lib/api';
import { Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface Playlist {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
}

interface PlaylistContentProps {
  selectedPlaylist: string | null;
  playlists: Playlist[];
  playlistVideos: {[key: string]: string[]};
  videos: Video[]; // Gardé pour la compatibilité mais non utilisé
  isLoading: boolean;
  onRemoveFromPlaylist: (videoId: string, playlistId: string) => void;
  onClearPlaylist?: (playlistId: string) => Promise<void>;
}

const PlaylistContent = ({ 
  selectedPlaylist, 
  playlists, 
  playlistVideos, 
  isLoading,
  onRemoveFromPlaylist,
  onClearPlaylist
}: PlaylistContentProps) => {
  const [isClearingPlaylist, setIsClearingPlaylist] = useState(false);
  const [playlistVideosList, setPlaylistVideosList] = useState<Video[]>([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    const fetchPlaylistVideos = async () => {
      if (!selectedPlaylist || !user) {
        setPlaylistVideosList([]);
        setLoadingVideos(false);
        return;
      }

      const playlistVideoIds = playlistVideos[selectedPlaylist] || [];
      
      if (playlistVideoIds.length === 0) {
        setPlaylistVideosList([]);
        setLoadingVideos(false);
        return;
      }

      try {
        setLoadingVideos(true);
        // Charger les vidéos de la playlist depuis user_videos
        const { data, error } = await supabase
          .from('user_videos')
          .select('*')
          .eq('added_by_user_id', user.id)
          .in('video_id', playlistVideoIds);

        if (error) throw error;

        // Convertir les données en format Video
        const videos: Video[] = data.map(item => ({
          id: item.video_id,
          title: item.title,
          thumbnail: item.thumbnail,
          channelName: item.channel_name,
          channelAvatar: item.channel_avatar,
          channelId: item.channel_id,
          views: item.views,
          published: item.published,
          publishedDate: item.published_date,
          duration: item.duration,
        }));

        setPlaylistVideosList(videos);
      } catch (error) {
        console.error('Error fetching playlist videos:', error);
        setPlaylistVideosList([]);
      } finally {
        setLoadingVideos(false);
      }
    };

    fetchPlaylistVideos();
  }, [selectedPlaylist, playlistVideos, user]);
  
  if (!selectedPlaylist) return null;
  
  if (isLoading || loadingVideos) {
    return <Loader size="large" />;
  }
  
  const playlistName = playlists.find(p => p.id === selectedPlaylist)?.name || 'Playlist';
  const playlistVideoIds = playlistVideos[selectedPlaylist] || [];
  
  // Function to clear all videos from a playlist
  const handleClearPlaylist = async () => {
    if (!onClearPlaylist || !selectedPlaylist) return;
    
    try {
      setIsClearingPlaylist(true);
      await onClearPlaylist(selectedPlaylist);
      
      // Cette notification devrait maintenant être déclenchée par le hook Realtime
    } catch (error) {
      console.error('Erreur lors de la suppression des vidéos:', error);
      toast({
        title: "Erreur",
        description: "Impossible de vider la playlist",
        variant: "destructive",
      });
    } finally {
      setIsClearingPlaylist(false);
    }
  };
  
  // Determine if we have playlist items but no matching videos
  const hasItemsButNoVideos = playlistVideoIds.length > 0 && playlistVideosList.length === 0;
  
  return (
    <>
      <div className="flex items-center justify-between mt-6 mb-4">
        <h3 className="text-xl font-medium">{playlistName}</h3>
        
        {playlistVideoIds.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={handleClearPlaylist}
            disabled={isClearingPlaylist}
          >
            {isClearingPlaylist ? (
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-youtube-red"></span>
            ) : (
              <X className="h-4 w-4" />
            )}
            Vider la playlist
          </Button>
        )}
      </div>
      
      {playlistVideosList.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {playlistVideosList.map((video) => (
            <div key={video.id} className="relative group">
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                onClick={() => onRemoveFromPlaylist(video.id, selectedPlaylist)}
                title="Retirer de la playlist"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <VideoCardWrapper {...video} />
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white dark:bg-youtube-dark shadow-sm rounded-lg border border-gray-200 dark:border-gray-800">
          <h2 className="text-xl font-medium mb-4">
            {hasItemsButNoVideos 
              ? "Vidéos non disponibles" 
              : "Playlist vide"}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            {hasItemsButNoVideos 
              ? "Les vidéos de cette playlist ne sont pas disponibles actuellement. Elles pourraient être privées ou supprimées."
              : "Cette playlist ne contient pas encore de vidéos. Parcourez les vidéos et ajoutez-les à cette playlist."}
          </p>
        </div>
      )}
    </>
  );
};

export default PlaylistContent;
